import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { fetchProducts } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export const Merch = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { dispatch } = useCart();

  useEffect(() => {
    fetchProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const add = (p) => {
    dispatch({ type: "add", product: p });
    toast.success(`${p.name} added to the tsunami.`, {
      description: "Tap cart to checkout.",
    });
  };

  return (
    <section id="merch" data-testid="merch-section" className="relative py-28 md:py-40 bg-ink-950">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="text-xs tracking-[0.4em] uppercase text-gold mb-3">
              ◢ Shop the Tsunami
            </div>
            <h2 className="font-display font-black uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              Wear the <span className="text-shimmer-gold">Storm</span>
            </h2>
          </div>
          <p className="font-serif italic text-slate-400 text-lg max-w-sm">
            Limited drops. Numbered editions. When it's gone, it's salt in the sea.
          </p>
        </div>

        {loading && (
          <div className="py-20 text-center text-slate-500 text-sm tracking-[0.3em] uppercase">
            Loading the drop…
          </div>
        )}

        <div
          data-testid="merch-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5"
        >
          {products.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              data-testid={`product-card-${p.id}`}
              className="group relative bg-ink-950 p-6 md:p-8 flex flex-col hover:bg-ink-900/60 transition-colors"
            >
              {p.badge && (
                <span className="absolute top-6 right-6 z-10 text-[9px] tracking-[0.3em] uppercase font-bold px-2 py-1 bg-gold text-ink-950">
                  {p.badge}
                </span>
              )}
              <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-ink-900 via-ink-950 to-ink-900 mb-6">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-glow mb-2">
                {p.category}
              </div>
              <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight mb-2 group-hover:text-gold transition-colors">
                {p.name}
              </h3>
              <p className="text-sm text-slate-400 font-light leading-relaxed mb-6 flex-1">
                {p.description}
              </p>
              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span className="font-display font-black text-2xl text-shimmer-gold">
                  ${p.price.toFixed(0)}
                </span>
                <button
                  data-testid={`add-to-cart-${p.id}`}
                  onClick={() => add(p)}
                  className="group/btn inline-flex items-center gap-2 px-5 py-3 bg-cyan-neon hover:bg-magenta-hot text-ink-950 hover:text-white font-bold uppercase tracking-[0.2em] text-[11px] transition-all shadow-[0_0_20px_rgba(6,182,212,0.4)] hover:shadow-[0_0_30px_rgba(219,39,119,0.6)]"
                >
                  Add to Cart
                  <Plus size={14} className="group-hover/btn:rotate-90 transition-transform" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
